/**
 * The projects register.
 *
 * Built like the clients register — see `clients-page.tsx` for why a page like
 * this is a column list and nothing else.
 *
 * The one thing worth saying here is the budget columns. `budget_minutes` and
 * `booked_minutes` are integers of minutes end to end, and the column that
 * reads "42h 30m" formats them at the edge. Storing hours as a float is how a
 * timesheet ends up half a minute short a hundred times.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, CalendarDays, FolderKanban, Plus, Tag } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatDuration,
  formatMoney,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import {
  BILLING_TYPE_LABELS,
  PROJECT_STATUS_TONES,
} from '../../lib/tones';
import { ProjectFormDialog } from './project-form-dialog';

export interface ProjectRow {
  public_id: string;
  code: string;
  name: string;
  status: string;
  billing_type: string;
  client_public_id: string;
  client_name: string;
  manager_name: string | null;
  department_name: string | null;
  starts_on: string | null;
  ends_on: string | null;
  currency: string;
  budget_amount: string | null;
  budget_minutes: number | null;
  booked_minutes: number;
  open_task_count: number;
  client_visible: boolean;
  created_at: string;
}

interface ProjectOptions {
  statuses: { value: string; label: string }[];
  billing_types: { value: string; label: string }[];
}

const ENDPOINT = '/projects';

export function ProjectsPage() {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api.get<ProjectOptions>(`${ENDPOINT}/options`),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<ProjectRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<ProjectRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Code',
        filter: 'text',
        width: 120,
        render: (row) => <span className="font-mono text-xs text-muted">{row.code}</span>,
      },
      {
        field: 'name',
        header: 'Project',
        icon: <FolderKanban className="size-3" />,
        filter: 'text',
        minWidth: 220,
        render: (row) => <span className="truncate font-medium">{row.name}</span>,
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 170,
      },
      {
        field: 'status',
        header: 'Status',
        icon: <Tag className="size-3" />,
        filter: 'select',
        options: options.data?.statuses,
        width: 120,
        render: (row) => <StatusChip value={row.status} tones={PROJECT_STATUS_TONES} />,
      },
      {
        field: 'billing_type',
        header: 'Billing',
        filter: 'select',
        options: options.data?.billing_types,
        width: 150,
        format: (value) => BILLING_TYPE_LABELS[value as string] ?? String(value),
      },
      { field: 'manager_name', header: 'Manager', filter: 'select', minWidth: 150 },
      {
        field: 'open_task_count',
        header: 'Open tasks',
        numeric: true,
        width: 110,
        // A correlated subquery, so it is not in the endpoint's sort or filter
        // whitelist — offering either would be silently ignored.
        sortable: false,
      },
      {
        field: 'booked_minutes',
        header: 'Booked',
        numeric: true,
        width: 110,
        sortable: false,
        render: (row) => (
          <span className="tabular">
            {formatDuration(row.booked_minutes)}
            {/* Against the budget, where there is one. A booked figure on its
                own does not answer the question anybody is asking. */}
            {row.budget_minutes ? (
              <span className="text-faint"> / {formatDuration(row.budget_minutes)}</span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'budget_amount',
        header: 'Budget',
        filter: 'number',
        numeric: true,
        width: 130,
        format: (value, row) => formatMoney(value as string | null, row.currency),
      },
      {
        field: 'starts_on',
        header: 'Starts',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 120,
        hiddenByDefault: true,
        format: (value) => formatDate(value as string | null),
      },
      {
        field: 'ends_on',
        header: 'Ends',
        filter: 'date',
        width: 120,
        format: (value) => formatDate(value as string | null),
      },
      {
        field: 'client_visible',
        header: 'Client sees it',
        filter: 'boolean',
        align: 'center',
        width: 120,
        hiddenByDefault: true,
        render: (row) =>
          row.client_visible ? (
            <StatusChip value="visible" tones={{ visible: 'info' }} />
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        field: 'created_at',
        header: 'Added',
        filter: 'date',
        width: 120,
        hiddenByDefault: true,
        format: (value) => formatDate(value as string | null),
      },
    ],
    [options.data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Projects"
        description="The work you are doing, and what it is costing."
        actions={
          <Can permission="projects.project.create">
            <Button size="sm" onClick={() => setFormOpen(true)}>
              <Plus className="size-3.5" />
              New project
            </Button>
          </Can>
        }
      />

      <ServerTable<ProjectRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['projects']}
        stateKey="projects.list"
        searchPlaceholder="Search by name, code or client"
        viewField="name"
        onView={(row) => navigate(`/projects/${row.public_id}`)}
        recentKey={recentKey}
        selectable
        onDelete={async (row) => {
          try {
            await api.delete(`${ENDPOINT}/${row.public_id}`);
            toast.success(`${row.name} deleted`);
          } catch (error) {
            // "…has booked time against it" names what is in the way, so it is
            // shown as written rather than replaced with a generic failure.
            toast.error(
              'Could not delete',
              error instanceof ApiError ? error.message : undefined,
            );
            throw error;
          }
        }}
        deleteLabel={(row) => row.name}
        rowDetailFields={(row) => [
          { label: 'Client', value: row.client_name },
          { label: 'Manager', value: row.manager_name },
          { label: 'Department', value: row.department_name },
          { label: 'Billing', value: BILLING_TYPE_LABELS[row.billing_type] ?? row.billing_type },
          { label: 'Starts', value: formatDate(row.starts_on) },
          { label: 'Ends', value: formatDate(row.ends_on) },
          { label: 'Budget', value: formatMoney(row.budget_amount, row.currency) },
          { label: 'Booked', value: formatDuration(row.booked_minutes) },
        ]}
        emptyTitle="No projects yet"
        emptyDescription="A project is a piece of work for a client. Time, tickets and invoices all hang off one."
        emptyAction={
          <Can permission="projects.project.create">
            <Button size="sm" onClick={() => setFormOpen(true)}>
              <Plus className="size-3.5" />
              Add a project
            </Button>
          </Can>
        }
      />

      <ProjectFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={(publicId) => {
          setRecentKey(publicId);
          setFormOpen(false);
        }}
      />
    </div>
  );
}
